/**
 * Redirect `/marketer-view` (no brand hash) → `/marketer-view/:brandHash`
 * (SYR-68). Prefers the brand saved in the shared Data Selection filters,
 * falling back to the first brand in the org.
 *
 * Also the onboarding entry point for the marketer-view shell: a user who
 * still needs onboarding (new org, no active surveys) gets the same
 * SimpleOnboardingWizardV2 the dashboard shows — so the flow keeps working
 * when /dashboard is swapped over to /marketer-view.
 */

import React, { useState } from 'react';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Modal, Spin } from 'antd';
import { ArrowRightOutlined, SafetyOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import { Utils } from '../../../utils/utils';
import { useMarketerRoot } from '../../../hooks/useDashboardConfig';
import {
  useGetBrandsQuery,
  useGetSurveysQuery,
  useCompleteOnboardingMutation,
} from '../../../services/nestjs-base-api';
import { SimpleOnboardingWizardV2 } from '../../SimpleOnboardingWizardV2';
import {
  isExploringOnboarding,
  setExploringOnboarding,
} from './onboardingExplore';

/**
 * `?onboarding=stepN` (step1..step3) → the wizard's 0-based forceStep index.
 * Lets you deep-link / QA a specific wizard step (notably the final share step)
 * without walking the whole flow. Returns undefined for anything else.
 */
const parseOnboardingStep = (raw: string | null): number | undefined => {
  if (!raw) return undefined;
  const match = /^step([1-3])$/i.exec(raw.trim());
  return match ? Number(match[1]) - 1 : undefined;
};

export const MarketerBrandRedirect: React.FunctionComponent = () => {
  const { user } = useAuth();
  const jwtToken = user?.jwtToken ?? '';
  const organisationId = user?.organisation?.id ?? 0;
  const [completeOnboarding] = useCompleteOnboardingMutation();

  // "Explore during onboarding" — when set, skip the wizard and let the user
  // into the shell (the shell shows a "Finish setup" banner to resume).
  const [exploring, setExploring] = useState(() =>
    isExploringOnboarding(user?.id),
  );
  // Once the wizard has shown, keep it mounted until the user completes it —
  // otherwise creating the survey in step 2 flips hasActiveSurveys, the gate
  // goes false, and the wizard unmounts before its final step ("How to get
  // things running") can render. Mirrors DashboardPage's onboardingWizardStarted.
  const [wizardStarted, setWizardStarted] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const marketerRoot = useMarketerRoot();
  const intl = useIntl();

  const { data: brands, isLoading: brandsLoading } = useGetBrandsQuery(
    { jwtToken, organisationId },
    { skip: !jwtToken || !organisationId },
  );

  const { data: surveysData, isLoading: surveysLoading } = useGetSurveysQuery(
    { jwtToken, organisationId },
    { skip: !jwtToken || !organisationId },
  );

  if (!user) {
    const redirectUrl = encodeURIComponent(marketerRoot);
    return <Navigate to={`/sign-in?redirect=${redirectUrl}`} replace />;
  }

  // Send non-org users to their own home — mirrors MarketerShell /
  // ModernDashboardLayout. Without this an agency (or platform admin) user who
  // lands on /dashboard gets stuck on the brands-loading spinner (their org id
  // is absent → the brands query is skipped → `!brands` never clears).
  if (Utils.userIsPlatformAdmin(user)) {
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (Utils.userIsAgencyUser(user) && !user.agencyContext) {
    return <Navigate to="/agency" replace />;
  }

  if (brandsLoading || surveysLoading || !brands) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 96 }}>
        <Spin size="large" />
      </div>
    );
  }

  // Onboarding gate — mirrors DashboardPage's shouldShowWizard: a user who
  // needs onboarding and has no active survey yet gets the wizard. (A newly
  // registered org already has a default brand, so this keys off surveys, not
  // brand count.)
  const hasActiveSurveys =
    surveysData !== undefined &&
    surveysData.length > 0 &&
    surveysData.some((s) => s.status === 'active');

  // `?onboarding=stepN` force-shows the wizard at that step regardless of
  // onboarding state (deep-link / QA of the final share step). forceStep=2
  // (step3) pulls the real survey share links when the org has active surveys.
  const forcedStep = parseOnboardingStep(searchParams.get('onboarding'));

  const shouldShowWizard =
    !!user.needsOnboarding && !hasActiveSurveys && !exploring;
  // Latch the wizard on the first render it should show (safe to set during
  // render — guarded so it can't loop), so it stays mounted through step 3.
  if (shouldShowWizard && !wizardStarted) {
    setWizardStarted(true);
  }

  if (
    forcedStep !== undefined ||
    ((shouldShowWizard || wizardStarted) && !exploring)
  ) {
    const handleOnboardingComplete = async () => {
      // Persist to the backend, then clear the local flags and reload so this
      // component re-evaluates (needsOnboarding=false + a survey now exists →
      // redirect to the brand's marketer view).
      try {
        if (jwtToken) {
          await completeOnboarding({ jwtToken }).unwrap();
        }
      } catch {
        /* non-fatal — the local flag + survey existence still gate correctly */
      }
      setExploringOnboarding(user.id, false);
      const updatedUser = { ...user, needsOnboarding: false };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      // When force-shown via ?onboarding=, drop the param so we don't loop back
      // into the wizard; otherwise reload in place.
      setTimeout(() => {
        if (forcedStep !== undefined) {
          window.location.href = marketerRoot;
        } else {
          window.location.reload();
        }
      }, 100);
    };

    // Option 2 escape hatch: let the user explore the platform first. Setting
    // the flag + local state falls through to the normal brand redirect (into
    // the shell), where a persistent "Finish setup" banner brings them back.
    // Skipping setup lands them in a data-less shell where the funnel data
    // shows they tour a few pages and leave. Don't make it a one-click reflex
    // sitting beside the primary action — surface the cost first so the choice
    // is informed, not accidental.
    const handleExplore = () => {
      Modal.confirm({
        title: intl.formatMessage({ id: 'marketerView.onboarding.skipConfirmTitle' }),
        content: intl.formatMessage({ id: 'marketerView.onboarding.skipConfirmBody' }),
        // Reassurance, not a warning: a shield in the wizard's own amber, not
        // the default red exclamation.
        icon: <SafetyOutlined style={{ color: '#F59E0B' }} />,
        // "Continue setup" is the emphasised primary (ok) so the reassurance
        // nudges toward finishing; "Skip anyway" is the quiet cancel. onOk is a
        // no-op that just closes and leaves them in the wizard; the skip lives
        // on cancel. keyboard/maskClosable off so Esc or a backdrop click keeps
        // them in setup rather than accidentally skipping.
        okText: intl.formatMessage({ id: 'marketerView.onboarding.skipConfirmContinue' }),
        cancelText: intl.formatMessage({ id: 'marketerView.onboarding.skipConfirmSkip' }),
        // Match the wizard's flat amber CTA rather than antd's default blue.
        okButtonProps: {
          style: {
            backgroundColor: '#F59E0B',
            borderColor: '#F59E0B',
            color: '#fff',
          },
        },
        maskClosable: false,
        keyboard: false,
        onCancel: () => {
          setExploringOnboarding(user.id, true);
          setExploring(true);
        },
      });
    };

    // Keep "View all surveys" inside the marketer-view shell instead of
    // bouncing out to /dashboard/surveys.
    const handleViewSurveys = () => {
      const hash = brands[0]?.hash;
      navigate(hash ? `${marketerRoot}/${hash}/admin/surveys` : marketerRoot);
    };

    return (
      // Immersive full-bleed "ink" canvas — slate-900 (#0F172A), the exact dark
      // the marketing site uses for its dark sections (bg-slate-900) and the
      // app's own textBase. Matching it gives visual continuity across the
      // site -> signup -> wizard journey. This is a full-page destination, not
      // a modal over content, so it's an OPAQUE background (never a scrim); the
      // amber hero and white cards pop against it for a focused, premium feel.
      <div
        style={{
          minHeight: '100vh',
          background: '#0F172A',
        }}
      >
        {forcedStep === undefined && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '12px 20px',
            }}
          >
            <Button
              type="text"
              size="small"
              onClick={handleExplore}
              style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 400 }}
            >
              <FormattedMessage
                id="marketerView.onboarding.explore"
                defaultMessage="Skip for now, explore the platform"
              />{' '}
              <ArrowRightOutlined />
            </Button>
          </div>
        )}
        <SimpleOnboardingWizardV2
          onComplete={handleOnboardingComplete}
          forceStep={forcedStep}
          onViewSurveys={handleViewSurveys}
        />
      </div>
    );
  }

  if (brands.length === 0) {
    // No brands — land on the shell which renders the "no brands" message.
    return <Navigate to={`${marketerRoot}/none`} replace />;
  }

  let target = brands[0];
  if (user.id) {
    try {
      const raw = localStorage.getItem(`brandEquityFilters_${user.id}`);
      const savedId = raw ? (JSON.parse(raw) as { brandId?: number }).brandId : undefined;
      const saved = savedId ? brands.find((b) => b.id === savedId) : undefined;
      if (saved) target = saved;
    } catch {
      /* fall back to first brand */
    }
  }

  return <Navigate to={`${marketerRoot}/${target.hash}`} replace />;
};
